// crmPipeline — pure helpers for the personal CRM relationship pipeline
// (MyCrmDashboard + RelationshipPipelineTable + FollowUpsSidebar).
//
// There is no "deal stage" field anywhere in the CRM data model (CrmContact
// has no `stage`, and CrmStats/CrmActivity don't track a sales funnel). To
// avoid fabricating a Negotiating/Engaged/Partner-style pipeline that the
// backend doesn't track, "relationship status" here is derived entirely from
// real, already-fetched signals: each contact's linked CrmTodo records
// (entity_type_id === "contact#<id>") and their real status/due_date.
import type { CrmContact, CrmTodo, Project } from '../types'

export type ContactStatus = 'needs-follow-up' | 'follow-up-scheduled' | 'no-follow-up'

export const CONTACT_STATUS_LABEL: Record<ContactStatus, string> = {
  'needs-follow-up': 'Needs follow-up',
  'follow-up-scheduled': 'Follow-up scheduled',
  'no-follow-up': 'No follow-up',
}

export const CONTACT_STATUS_BADGE: Record<ContactStatus, string> = {
  'needs-follow-up': 'bg-rose-500/20 text-rose-700',
  'follow-up-scheduled': 'bg-sky-500/20 text-sky-700',
  'no-follow-up': 'bg-slate-500/10 text-slate-500',
}

/** Today as a plain "YYYY-MM-DD" string (UTC), so due_date strings — which are
 * date-only, no time component — can be compared lexically without any of
 * the timezone-rollback pitfalls `new Date(dueDate)` introduces (see BUG-158
 * in MyTodos.tsx). */
export function todayDateString(): string {
  return new Date().toISOString().slice(0, 10)
}

export function isOverdue(dueDate: string | undefined | null, today: string = todayDateString()): boolean {
  if (!dueDate) return false
  return dueDate < today
}

export function contactEntityId(contactId: string): string {
  return `contact#${contactId}`
}

export function todosForContact(todos: CrmTodo[], contactId: string): CrmTodo[] {
  const key = contactEntityId(contactId)
  return todos.filter((t) => t.entity_type_id === key)
}

export function openTodosForContact(todos: CrmTodo[], contactId: string): CrmTodo[] {
  return todosForContact(todos, contactId).filter((t) => t.status === 'open' || t.status === 'in_progress')
}

export function contactStatus(todos: CrmTodo[], contactId: string, today: string = todayDateString()): ContactStatus {
  const open = openTodosForContact(todos, contactId)
  if (open.length === 0) return 'no-follow-up'
  if (open.some((t) => isOverdue(t.due_date, today))) return 'needs-follow-up'
  return 'follow-up-scheduled'
}

/** Most recent real timestamp on record for a contact: either an edit to the
 * contact itself, or the most recent create/update on a to-do linked to
 * them. Returns null when there is genuinely nothing to show (never a
 * fabricated date). */
export function lastTouch(contact: CrmContact, todos: CrmTodo[]): string | null {
  const related = todosForContact(todos, contact.contact_id)
  const dates = [contact.updated_at, ...related.map((t) => t.updated_at || t.created_at)].filter(
    (d): d is string => !!d
  )
  if (dates.length === 0) return null
  return dates.reduce((max, d) => (d > max ? d : max))
}

/** Open/in-progress to-dos that have a due date, soonest first — the set
 * shown in the "Follow-ups due" sidebar. */
export function dueFollowUps(todos: CrmTodo[]): CrmTodo[] {
  return todos
    .filter((t) => !!t.due_date && (t.status === 'open' || t.status === 'in_progress'))
    .sort((a, b) => (a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0))
}

export function overdueCount(todos: CrmTodo[], today: string = todayDateString()): number {
  return dueFollowUps(todos).filter((t) => isOverdue(t.due_date, today)).length
}

/** Contacts with at least one open/in-progress to-do attached — the real,
 * computed basis for the "In pipeline" KPI (no fabricated funnel stages). */
export function contactsInPipelineCount(contacts: CrmContact[], todos: CrmTodo[]): number {
  return contacts.filter((c) => openTodosForContact(todos, c.contact_id).length > 0).length
}

export function distinctProjectCount<T extends { project_id: string }>(items: T[]): number {
  return new Set(items.map((i) => i.project_id).filter(Boolean)).size
}

/** Resolve a contact/org's project_id to a human name via the user's project
 * list. Falls back to null (never a fabricated name) when the project can't
 * be resolved (e.g. access lost, or still loading). */
export function projectNameFor(projects: Project[], projectId: string | undefined | null): string | null {
  if (!projectId) return null
  return projects.find((p) => p.project_id === projectId)?.name ?? null
}

/** Safe display name for a contact. The CRM legitimately holds contacts with
 * no first_name/last_name (org-only rows, directory-copy imports,
 * ai_ecosystem_match suggestions) — naively interpolating
 * `${first_name} ${last_name}` renders the literal string "undefined
 * undefined" for those. Falls back through organisation → email → a plain
 * label, in that order, and NEVER renders "undefined". */
export function contactDisplayName(
  c: Pick<CrmContact, 'first_name' | 'last_name' | 'organisation_name' | 'email'>
): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
  if (name) return name
  if (c.organisation_name) return c.organisation_name
  if (c.email) return c.email
  return 'Unnamed contact'
}

/** Up to two-letter avatar initials derived from the same safe display name
 * used everywhere else, so the avatar badge and the name it sits next to are
 * always consistent (and never "undefined"). */
export function contactInitials(
  c: Pick<CrmContact, 'first_name' | 'last_name' | 'organisation_name' | 'email'>
): string {
  if (c.first_name || c.last_name) {
    return [c.first_name?.[0], c.last_name?.[0]].filter(Boolean).join('')
  }
  const name = contactDisplayName(c)
  return name === 'Unnamed contact' ? '?' : name[0]?.toUpperCase() || '?'
}

export function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Format a plain date-only ("YYYY-MM-DD") due_date string, verbatim in UTC —
 * same fix as MyTodos.tsx BUG-158, reused here so the sidebar never rolls
 * the date back a day in negative-offset timezones. */
export function formatDueDate(dueDate: string | null | undefined): string | null {
  if (!dueDate) return null
  const d = new Date(dueDate)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}
